import type { DataSession } from "../data/core/session";
import { useData } from "../runtime/provider";
import { createSearchSessions } from "@libs/rudderstack/search-session";

import { debounce } from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";

type SearchProcedure = DataSession["utils"]["catalog"]["searchAnything"];

export const SEARCH_DEBOUNCE_MS = 150;
export const SEARCH_MAX_WAIT_MS = 300;

type SearchResponse<T extends SearchProcedure> = Awaited<
  ReturnType<T["fetch"]>
>;

/** The search box, so Search Intents can be read per surface. */
export type SearchSurface = NonNullable<
  Parameters<SearchProcedure["fetch"]>[0]["searchSession"]
>["surface"];

type SelectedSearchResult = Pick<
  SearchResponse<SearchProcedure>["searchResults"][number],
  "id" | "type"
>;

export const useSearch = <T extends SearchProcedure>(
  procedure: T,
  surface: SearchSurface,
) => {
  const session = useData();
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResponse, setSearchResponse] = useState<SearchResponse<T>>();
  const [isSearching, setIsSearching] = useState(false);

  const searchTermRef = useRef(searchTerm);
  const procedureRef = useRef(procedure);
  const [searchSessions] = useState(createSearchSessions);
  // The term whose results are on screen: a pick is recorded against it.
  const shownTermRef = useRef("");

  useEffect(() => {
    procedureRef.current = procedure;
  }, [procedure]);

  const runSearch = useCallback(
    async (searchTermInput: string) => {
      try {
        const searchResponse = (await procedureRef.current.fetch({
          searchTerm: searchTermInput,
          searchSession: {
            id: searchSessions.forRequest(searchTermInput),
            surface,
          },
        })) as SearchResponse<T>;

        if (searchResponse.searchTerm === searchTermRef.current) {
          shownTermRef.current = searchResponse.searchTerm;
          setSearchResponse(searchResponse);
          setIsSearching(false);
        }
      } catch (error) {
        console.error(error);
        setIsSearching(false);
      }
    },
    [searchSessions, surface],
  );

  // Reports that a result was picked, which is what makes the search "found".
  const recordSearchSelection = useCallback(
    (result: SelectedSearchResult, position?: number) => {
      const shownTerm = shownTermRef.current;
      if (shownTerm.trim().length === 0) return;
      session.api.analytics.recordSearchSelection
        .mutate({
          searchSession: { id: searchSessions.forPick(shownTerm), surface },
          searchTerm: shownTerm,
          resultType: result.type,
          resultId: result.id,
          position,
        })
        .catch(console.error);
    },
    [session, searchSessions, surface],
  );

  const debouncedSearchRef = useRef<ReturnType<
    typeof debounce<typeof runSearch>
  > | null>(null);

  useEffect(() => {
    const debounced = debounce(runSearch, SEARCH_DEBOUNCE_MS, {
      maxWait: SEARCH_MAX_WAIT_MS,
      trailing: true,
    });
    debouncedSearchRef.current = debounced;
    return () => {
      debounced.cancel();
      debouncedSearchRef.current = null;
    };
  }, [runSearch]);

  const searchAll = useCallback(
    (searchTermInput: string) => debouncedSearchRef.current?.(searchTermInput),
    [],
  );

  const setSearchTermInput = useCallback(
    async (searchTermInput: string) => {
      setSearchTerm(searchTermInput);
      searchTermRef.current = searchTermInput;

      if (searchTermInput.length === 0) {
        shownTermRef.current = "";
        setSearchResponse(undefined);
      } else {
        setIsSearching(true);
        await searchAll(searchTermInput);
      }
    },
    [searchAll],
  );

  return {
    isSearching,
    recordSearchSelection,
    searchResults: (searchResponse?.searchResults ??
      []) as SearchResponse<T>["searchResults"],
    searchTerm,
    setSearchTermInput,
  };
};
